// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DynamicThresholdGridAction.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------
import { compose, withHandlers, withState } from 'recompose';
import { isEmpty, curry, cloneDeep, find, map, isNil, head, getOr, findIndex, get, filter } from 'lodash/fp';
import ThresholdsDataObject from 'logic/models/Izenda.ThresholdsDataObject';
import { updateArray, sortArray } from 'share/utils/Izenda.ArrayUtils';
import ReportPartFieldConstants from 'logic/data/Izenda.ReportPartFieldConstants';
import { isObservableArray } from 'mobx';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';

const {
  SORT_ORDERS: { DESC }
} = ReportPartFieldConstants;

/**
 * Check compatible function
 * @param  {Object} props
 * @param  {array} functions
 * @param  {object} field
 * @return {boolean}
 */
const checkCompatibleFunction = (props: any, functions: Array<any>, field: any): boolean => {
  const reportPartDetails = props.reportPartDetails;
  const reportPartContent = reportPartDetails.reportPartContent;
  const metricContainer = get(reportPartContent.getMetricContainer(), reportPartContent);
  return filter(
    reportPartContent.checkedCompatibleFunction.bind(reportPartContent, metricContainer, {
      field: field
    }),
    functions
  );
};

/**
 * Sync dynamic threshold elements.
 */
const syncDynamicThreholdsElemnts = props => {
  props.validation.reset();
  //propertiesEditorModel.dataSchema. dataFormattings.color
  props.onChange({ value: props.thresholdData }, 'dynamicThresholds');
};

/**
 * Delete threshold
 * @param {object} props
 * @param {string} value
 */
const deleteThreshold = (props: any, value: string): void => {
  const data = props.thresholdData;

  const deleteValue = get(value, data);
  const selectedElement = get('thresholdFieldElement', deleteValue);

  // remove field in value
  if (selectedElement) {
    const reportPartDetails = props.reportPartDetails;
    const reportPartContent = reportPartDetails.reportPartContent;
    const dynamicThreshold = find({ name: 'dynamicThresholds' }, reportPartContent['customContainers']);
    reportPartDetails.removeFieldFromContainer(dynamicThreshold, selectedElement);
  }
  data.splice(value, 1);
  props.updateThresholdData(data);
  // call function sync
  syncDynamicThreholdsElemnts(props);
};

/**
 * Copy threshold
 * @param {object} props
 * @param {object} value
 */
const copyThreshold = (props: any, value: any) => {
  const thresholdFieldNameAlias = String.newGuid();
  const fieldStore = props.fieldStore;
  const data = props.thresholdData;
  const copyItem = cloneDeep(data[value]);
  const selectedElement = copyItem.thresholdFieldElement;

  if (selectedElement) {
    const reportPartDetails = props.reportPartDetails;
    const reportPartContent = reportPartDetails.reportPartContent;
    const dynamicThreshold = find({ name: 'dynamicThresholds' }, reportPartContent['customContainers']);

    const selectField = find({ id: selectedElement.field.fieldId }, props.dataTree);
    const dataType = selectedElement.field.dataFieldType;
    ReportPartUtils.getFunction(0, dataType).then(result => {
      const functions = checkCompatibleFunction(props, result, selectField);
      const functionData = find({ id: copyItem.functionId }, functions);
      reportPartDetails
        .insertFieldToContainer(dynamicThreshold, selectField.field, selectField.querySourceDetail, null, functionData)
        .then(newElement => {
          newElement.name = thresholdFieldNameAlias;
          newElement.field.fieldNameAlias = thresholdFieldNameAlias;
          copyItem.updateFieldName(thresholdFieldNameAlias);
          return newElement;
        })
        .then(copyItem.updateElement.bind(copyItem))
        .then(() => {
          reportPartDetails.useForThreshold = true;
          reportPartDetails.refreshDataProcess = true;
          copyItem.possion = props.maxPosition + 1;
          props.updateMaxPosition(copyItem.possion);
          data.unshift(copyItem);
          return fieldStore.needUpdatePreview(reportPartDetails);
        })
        .then(syncDynamicThreholdsElemnts.bind(null, props));
    });
  } else {
    copyItem.updateFieldName(thresholdFieldNameAlias);
    copyItem.possion = props.maxPosition + 1;
    props.updateMaxPosition(copyItem.possion);
    data.unshift(copyItem);
    // update threshold list
    props.updateThresholdData(data);
    syncDynamicThreholdsElemnts(props);
  }
};

/**
 * Update selected fields
 * @param {Object} props
 * @param {Object} value
 */
const updateSelectedFields = (props: any, fieldId: string, querySourceId: string): any => {
  const selectedField = find(
    field => field.id === fieldId && field.querySourceDetail.id === querySourceId,
    props.dataTree
  );
  const data = props.selectedField;
  data.push(selectedField);
  props.updateSelectedField(data);
  props.onChange({ value: props.thresholdData, selectedField: props.selectedField }, 'dynamicThresholds');
  return selectedField;
};

const addNewThreshold = (props: any, event: any) => {
  const data = props.thresholdData;
  const newObject = new ThresholdsDataObject();
  const metric = head(props.options);
  newObject.updateFieldMappingAlias(metric.value);
  newObject.possion = props.maxPosition + 1;
  props.updateMaxPosition(newObject.possion);
  data.unshift(newObject);
  props.updateThresholdData(data);
  props.onChange({ value: props.thresholdData }, 'dynamicThresholds');
};

/**
 * Sort threshold
 * @param {Array} thresholdData
 * @param {String} key
 * @param {String} direction
 */
const sortThresholds = (thresholdData: Array<any>, key: string, direction: string): void => {
  let sortedItems = [];
  if (isEmpty(direction)) {
    sortedItems = sortArray(thresholdData, [{ key: 'possion', descending: true }]);
  } else {
    sortedItems = sortArray(thresholdData, [{ key, descending: direction === DESC.value }]);
  }

  updateArray(thresholdData, sortedItems, item => item);
};

/**
 *
 * @param {Object} props
 * @param {String} columnKey
 * @param {Array} sortDir
 */
const onSortChange = (props: any, columnKey: string, sortDir: Array<any>): void => {
  const { thresholdData } = props;

  if (columnKey !== 'Field') return;
  sortThresholds(thresholdData, 'fieldMappingAlias', sortDir);
  props.updateThresholdData(thresholdData);
};

/**
 * Change dash type
 * @param {Object} props
 * @param {Object} rowData
 * @param {Object} changeData
 */
const onChangeDashType = (props: any, rowData: any, changeData: any): void => {
  rowData.updateDashStyle(changeData);
  syncDynamicThreholdsElemnts(props);
};

/**
 * Add dynamic threshold field
 * @param {Object} props
 * @param {Object} data
 * @param {Object} changeData
 */
const addDynamicThreholdsField = (props: any, data: ThresholdsDataObject, changeData: any): void => {
  const { fieldId, functionId, functionData, format, querySourceId } = changeData;
  const functionName = get('name', functionData);
  data.updateField(fieldId, fieldId, functionId, functionName, format, querySourceId);
  
  // Add filed here
  const fieldStore = props.fieldStore;
  const reportPartDetails = props.reportPartDetails;
  const reportPartContent = reportPartDetails.reportPartContent;
  
  // get container remove old threshold element & insert new element
  const dynamicThreshold = find({ name: 'dynamicThresholds' }, reportPartContent['customContainers']);
  const oldElement = data.thresholdFieldElement || {};
  if (oldElement) {
    const elementToRemove = find(ele => ele.fieldNameAlias === oldElement.fieldNameAlias, dynamicThreshold.elements);
    elementToRemove && reportPartDetails.removeFieldFromContainer(dynamicThreshold, elementToRemove);
  }
  
  // add field to container
  const selectField = updateSelectedFields(props, fieldId, querySourceId);
  reportPartDetails
    .insertFieldToContainer(dynamicThreshold, selectField.field, selectField.querySourceDetail, null, functionData)
    .then(newElement => {
      const thresholdFieldNameAlias = String.newGuid();
      newElement.name = thresholdFieldNameAlias;
      newElement.field.fieldNameAlias = thresholdFieldNameAlias;
      data.updateFieldName(thresholdFieldNameAlias);
      return newElement;
    })
    .then(data.updateElement.bind(data))
    .then(() => {
      reportPartDetails.useForThreshold = true;
      reportPartDetails.refreshDataProcess = true;
      return fieldStore.needUpdatePreview(reportPartDetails);
    })
    .then(syncDynamicThreholdsElemnts.bind(null, props));
};

export default compose(
  withState('thresholdData', 'updateThresholdData', props => {
    let i = 0;
    const data = map(function(item) {
      if (!isNil(item)) {
        isNil(item.possion) && (item.possion = i);
        i++;
        const obj = new ThresholdsDataObject(item);
        obj.updateElement(item.thresholdFieldElement);
        return obj;
      }
    }, props.value);

    sortThresholds(data, 'possion', DESC.value);

    return data;
  }),
  withState('maxPosition', 'updateMaxPosition', ({ value }) => {
    if (isNil(value) || !isObservableArray(value)) return 0;
    return value.slice().reduce((maxPossion, item) => Math.max(maxPossion, getOr(0, 'possion', item)), 0);
  }),
  withState('selectedField', 'updateSelectedField', () => {
    return [];
  }),
  withHandlers({
    onSortChange: curry(onSortChange),
    deleteThreshold: curry(deleteThreshold),
    copyThreshold: curry(copyThreshold),
    updateSelectedFields: curry(updateSelectedFields),
    addNewThreshold: curry(addNewThreshold),
    addDynamicThreholdsField: curry(addDynamicThreholdsField),
    onChangeDashType: curry(onChangeDashType)
  })
);



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.DynamicThresholdGridAction.js